import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAvatarUrl,
  updateAvatarUrl,
  MembersReviewState
} from 'slices/NdisSlices/platformAdmin/MembersReviewFormSlice';
import { RootState, AppDispatch } from 'store';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { styled, useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import {
  FiEdit2,
  FiMail,
  FiPhone,
  FiMapPin,
  FiUser,
  FiFileText,
  FiBriefcase,
  FiLock,
  FiSettings,
  FiAward,
  FiBook
} from 'react-icons/fi';
import { FaLinkedin, FaTwitter, FaGithub } from 'react-icons/fa';
import { fetchUserById, selectProfileSettings } from 'slices/ProfileSettingsSlice';

// Styled components
const ProfileCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6]
  }
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  '& svg': {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
    marginTop: theme.spacing(0.3),
    flexShrink: 0
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1)
  }
}));

const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightMedium,
  minHeight: 48,
  minWidth: 120,
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
    padding: theme.spacing(1)
  }
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-around',
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`
}));

const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  '& .stat-value': {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.primary.main
  },
  '& .stat-label': {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  }
}));

const SocialLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(2),
  '& a': {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 1),
    transition: 'color 0.3s ease',
    '&:hover': {
      color: theme.palette.primary.main
    }
  }
}));

// Define ProfileData interface to match component expectations
interface ProfileData {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  avatar: string;
}

const UsersSocialProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const { user, loading, error } = useSelector(selectProfileSettings);
  const { avatarLoading, avatarError, avatarUrls } = useSelector(
    (state: RootState) => state.membersReview as MembersReviewState
  );
  const [value, setValue] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'success'>('error');
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const avatarBlobUrls = useRef<{ [id: string]: string }>({});

  const userDetails = localStorage.getItem('user-details');
  const loginDetails = userDetails ? JSON.parse(userDetails) : {};
  const userId = loginDetails.userId || '';
  // Map API User to ProfileData
  const mapUserToProfileData = (user: any): ProfileData => ({
    id: user.id || '',
    name: user.user_details?.name || 'JWT User',
    email: user.user_details?.email || 'demo@sample.com',
    phone: user.phone || '(+99) 9999 999 999',
    address: user.address || 'Melbourne, Australia',
    avatar: user.avatar || ''
  });

  useEffect(() => {
    dispatch(fetchUserById(userId)).then((action) => {
      if (fetchUserById.fulfilled.match(action)) {
        console.log('Profile data fetched successfully:', action.payload);
        const mappedData = mapUserToProfileData(action.payload);
        setProfileData(mappedData);
      } else if (fetchUserById.rejected.match(action)) {
        console.error('Failed to fetch profile data:', action.payload);
        setSnackbarMessage(action.payload || 'Failed to fetch profile data');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    });
  }, [dispatch]);

  // Fetch avatar if available
  useEffect(() => {
    const fetchAvatar = async () => {
      if (profileData?.id) {
        try {
          const url = await getAvatarUrl(profileData.avatar || '');
          dispatch(updateAvatarUrl({ id: profileData.id, url }));
          avatarBlobUrls.current[profileData.id] = url;
        } catch (err) {
          console.error(`Failed to fetch avatar for user ${profileData.id}:`, err);
          setSnackbarMessage('Failed to load avatar');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
    };
    fetchAvatar();
  }, [profileData]);

  // Handle errors
  useEffect(() => {
    if (error || avatarError) {
      setSnackbarMessage(error || avatarError || 'An error occurred');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [error, avatarError]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleAvatarError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.onerror = null;
    target.src = '/default-avatar.png';
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!profileData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
          No user profile found
        </Typography>
      </Container>
    );
  }

  console.log('profileData', profileData);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%', boxShadow: 3 }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4, overflowX: 'auto' }}>
        <Tabs
          value={value}
          onChange={handleTabChange}
          aria-label="profile tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <CustomTab icon={<FiUser size={18} />} label="Profile" />
          <CustomTab icon={<FiFileText size={18} />} label="Personal Details" />
          <CustomTab icon={<FiBriefcase size={18} />} label="My Account" />
          <CustomTab icon={<FiLock size={18} />} label="Change Password" />
          <CustomTab icon={<FiSettings size={18} />} label="Settings" />
        </Tabs>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProfileCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                <Avatar
                  src={
                    profileData.id
                      ? avatarUrls[profileData.id] || '/default-avatar.png'
                      : '/default-avatar.png'
                  }
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    border: `3px solid ${theme.palette.primary.light}`,
                    boxShadow: theme.shadows[2]
                  }}
                  imgProps={{ onError: handleAvatarError }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<FiEdit2 size={16} />}
                  sx={{
                    position: 'absolute',
                    bottom: -12,
                    right: -12,
                    borderRadius: '50%',
                    minWidth: 40,
                    height: 40,
                    padding: 0,
                    '& .MuiButton-startIcon': {
                      margin: 0
                    }
                  }}
                />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {profileData.name}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Senior UI/UX Designer
              </Typography>

              <Button variant="outlined" color="primary" size="small" sx={{ mb: 3 }}>
                Upgrade to Pro
              </Button>

              <Box sx={{ textAlign: 'left', mt: 2 }}>
                <InfoItem>
                  <FiMail size={18} />
                  <Typography>{profileData.email}</Typography>
                </InfoItem>
                <InfoItem>
                  <FiPhone size={18} />
                  <Typography>{profileData.phone}</Typography>
                </InfoItem>
                <InfoItem>
                  <FiMapPin size={18} />
                  <Typography>{profileData.address}</Typography>
                </InfoItem>
              </Box>

              <StatsContainer>
                <StatItem>
                  <span className="stat-value">37</span>
                  <span className="stat-label">Mails</span>
                </StatItem>
                <StatItem>
                  <span className="stat-value">2.7k</span>
                  <span className="stat-label">Followers</span>
                </StatItem>
                <StatItem>
                  <span className="stat-value">678</span>
                  <span className="stat-label">Following</span>
                </StatItem>
              </StatsContainer>

              <SocialLinks>
                <a href="#" aria-label="LinkedIn">
                  <FaLinkedin size={20} />
                </a>
                <a href="#" aria-label="Twitter">
                  <FaTwitter size={20} />
                </a>
                <a href="#" aria-label="GitHub">
                  <FaGithub size={20} />
                </a>
              </SocialLinks>
            </CardContent>
          </ProfileCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <ProfileCard>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3
                }}
              >
                <SectionTitle>
                  <FiUser size={20} />
                  About Me
                </SectionTitle>
                <Button
                  variant="outlined"
                  startIcon={<FiEdit2 size={16} />}
                  size="small"
                  color="primary"
                >
                  Edit Profile
                </Button>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                Hello, I'm {profileData.name}, a Senior UI/UX Designer with over 8 years of
                experience creating digital products that are both beautiful and usable. I
                specialize in user-centered design approaches that solve real business problems
                while delivering exceptional user experiences. Currently based in{' '}
                {profileData.address}, I've worked with clients across various industries to create
                meaningful digital experiences.
              </Typography>
            </CardContent>
          </ProfileCard>

          <ProfileCard>
            <CardContent>
              <SectionTitle>
                <FiFileText size={20} />
                Personal Details
              </SectionTitle>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Full Name
                    </Typography>
                    <Typography>{profileData.name}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date of Birth
                    </Typography>
                    <Typography>{'15 March 1990'}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Phone
                    </Typography>
                    <Typography>{profileData.phone}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Email
                    </Typography>
                    <Typography>{profileData.email}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Address
                    </Typography>
                    <Typography>{profileData.address}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Nationality
                    </Typography>
                    <Typography>Australian</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </ProfileCard>

          <ProfileCard>
            <CardContent>
              <SectionTitle>
                <FiAward size={20} />
                Education
              </SectionTitle>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Master of Design (MDes)
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  University of Melbourne • 2014 - 2016
                </Typography>
                <Typography variant="body2">
                  Specialized in Human-Computer Interaction and User Experience Design.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Bachelor of Fine Arts (BFA)
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  RMIT University • 2010 - 2014
                </Typography>
                <Typography variant="body2">
                  Major in Graphic Design with minor in Digital Media.
                </Typography>
              </Box>
            </CardContent>
          </ProfileCard>

          <ProfileCard>
            <CardContent>
              <SectionTitle>
                <FiBriefcase size={20} />
                Work Experience
              </SectionTitle>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Senior UI/UX Designer
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  Digital Agency Inc. • 2019 - Present
                </Typography>
                <Typography variant="body2">
                  Lead designer for enterprise clients, managing a team of 5 designers.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  UI Designer
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  Creative Studio • 2016 - 2019
                </Typography>
                <Typography variant="body2">
                  Designed interfaces for web and mobile applications for various clients.
                </Typography>
              </Box>
            </CardContent>
          </ProfileCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UsersSocialProfile;
